import { useEffect } from 'react';

interface SchemaOrgData {
  '@context': string;
  '@type': string;
  [key: string]: unknown;
}

export const useSchemaOrg = (data: SchemaOrgData) => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    script.id = 'schema-org-jsonld';
    
    const existingScript = document.getElementById('schema-org-jsonld');
    if (existingScript) {
      existingScript.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById('schema-org-jsonld');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [data]);
};

export const getOrganizationSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://olharcriativo.pt';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Olhar Criativo',
    description: 'Agência de marketing digital e produção criativa especializada em transformar marcas em referências de mercado através de estratégia, design e conteúdo profissional.',
    url: siteUrl,
    logo: 'https://static.readdy.ai/image/f2bd418b0bfbfafabfbde3d1f67690ab/df5b1e398d566a2d008bac1d49647d35.png',
    image: 'https://static.readdy.ai/image/f2bd418b0bfbfafabfbde3d1f67690ab/df5b1e398d566a2d008bac1d49647d35.png',
    telephone: '+351963467122',
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'Algarve',
      addressCountry: 'PT'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '37.0194',
      longitude: '-7.9322'
    },
    areaServed: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: '37.0194',
        longitude: '-7.9322'
      },
      geoRadius: '50000'
    },
    sameAs: [
      siteUrl
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+351963467122',
      contactType: 'customer service',
      availableLanguage: ['pt', 'en', 'es', 'fr']
    }
  };
};

export const getWebPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://olharcriativo.pt';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Algarve Marketing Digital Gestão Redes Sociais Olhar Criativo',
    description: 'Olhar Criativo - Agência de marketing digital no Algarve especializada em gestão de redes sociais, produção de conteúdo profissional, branding e tráfego pago.',
    url: siteUrl,
    inLanguage: ['pt', 'en', 'es', 'fr'],
    isPartOf: {
      '@type': 'WebSite',
      name: 'Olhar Criativo',
      url: siteUrl
    },
    about: {
      '@type': 'Organization',
      name: 'Olhar Criativo'
    },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/f2bd418b0bfbfafabfbde3d1f67690ab/df5b1e398d566a2d008bac1d49647d35.png'
    }
  };
};

export const getServiceSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://olharcriativo.pt';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Marketing Digital',
    provider: {
      '@type': 'Organization',
      name: 'Olhar Criativo',
      url: siteUrl
    },
    areaServed: {
      '@type': 'Place',
      name: 'Algarve, Portugal'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Serviços de Marketing Digital',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Gestão de Redes Sociais',
            description: 'Estratégia completa para Instagram, Facebook e TikTok'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Produção de Conteúdo',
            description: 'Fotografia e vídeo profissional'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Design Gráfico',
            description: 'Design estratégico e identidade visual'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Branding',
            description: 'Construção de marcas memoráveis'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Tráfego Pago',
            description: 'Campanhas de Meta Ads e Google Ads'
          }
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Estratégia Digital',
            description: 'Planeamento completo de marketing digital'
          }
        }
      ]
    }
  };
};

export const getFAQSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Que serviços oferece a Olhar Criativo?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A Olhar Criativo oferece gestão de redes sociais, produção de conteúdo audiovisual, design gráfico, branding, tráfego pago e estratégia digital completa para marcas que querem crescer com profissionalismo.'
        }
      },
      {
        '@type': 'Question',
        name: 'Onde está localizada a Olhar Criativo?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A Olhar Criativo está localizada no Algarve, Portugal, e atende clientes em toda a região e além.'
        }
      },
      {
        '@type': 'Question',
        name: 'Como posso pedir um orçamento?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Pode pedir um orçamento através do formulário no nosso site, pelo WhatsApp (+351 963 467 122) ou através do assistente virtual disponível no site.'
        }
      }
    ]
  };
};
